// BUILD-046 Owner-requested layout revision (visual edits ledger, Layout-1 and Layout-5): the tabs of the My Work page.
//
// Order and labels (Owner's list): My Tasks | Shared Tasks | Assigned by Me | Team (a manager) or Company (the company holder). The last exists only for a Caller the
// SERVER says can oversee (hostViewsModel: `canOversee`); "Assigned by Me" is the existing "Requests I issued" view (GET /work/issued), shown for every Caller of the
// new routes. In the legacy page (the routes are off) there is no "Assigned by Me" and the tabs are those of hostViewsModel.
//
// A tab key is a HostView, or 'issued'. This module only NAMES the tabs: which list a tab shows, and every count, is decided elsewhere (viewCountsModel.describeTabCounts).
// Each tab has a visible label, an optional visible count, and an accessible name that says what the count counts ("My Tasks, 4 open").
//
// Pure module. Type-only imports. Erasable TypeScript only.

import type { HostMode, HostView, HostViews } from './hostViewsModel';
import type { TabCount, TabCounts } from './viewCountsModel';

export type PageTabKey = HostView | 'issued';

export interface PageTab {
  key: PageTabKey;
  /** Visible label. */
  label: string;
  /** The count shown beside the label, or null when there is none to show. */
  count: TabCount | null;
  /** Accessible name: the label, and the scoped count phrase when there is a count. */
  accessibleName: string;
  /** Tooltip: what the tab holds (and, for Team / Company, whose tasks). */
  title: string;
  /** DOM id of the tab (the panel is labelled by it). */
  id: string;
  /**
   * True only when the page said this tab's count is not known yet (Team / Company before its list has loaded for THIS Caller) and there is no number to show: the tab then draws a small
   * dash instead of nothing, and its accessible name says the count is not loaded. Absent on every other tab. A dash is never a zero: a real zero is a count of 0.
   */
  countPending?: true;
}

export const TAB_LABELS: Readonly<Record<PageTabKey, string>> = Object.freeze({
  'my-work': 'My Tasks',
  'group-shared': 'Shared Tasks',
  issued: 'Assigned by Me',
  'team-work': 'Team',
});

export const COMPANY_TAB_LABEL = 'Company';

export interface PageTabsInput {
  mode: HostMode;
  /** The host's tabs (hostViewsModel). Only their keys are read. */
  tabs: HostViews['tabs'];
  /** From `GET /work/me` `companyOversight`: the last tab reads Company instead of Team. */
  companyOversight: boolean;
  counts: TabCounts;
  /**
   * Tabs whose count is not known YET (the page holds no list for this Caller and has not asked: Team / Company is fetched only when its tab is opened, because filling the number would scan
   * every report). Only a tab that is already offered can be marked (this never adds a tab), and a real count always wins over the mark. Absent = no tab is marked, exactly as before.
   */
  countPending?: readonly PageTabKey[];
}

/** What the accessible name and the tooltip say while a count is not known. It never says zero, and it never says the person has no tasks. */
export const COUNT_PENDING_PHRASE = 'count not loaded yet';

const TITLES: Readonly<Record<PageTabKey, string>> = Object.freeze({
  'my-work': 'Tasks assigned to you',
  'group-shared': 'Tasks shared with you that you can take',
  issued: 'Requests you assigned to other people',
  'team-work': 'Tasks of the people you oversee',
});

function countOf(key: PageTabKey, counts: TabCounts): TabCount | null {
  return key === 'my-work' ? counts.mine : key === 'group-shared' ? counts.shared : key === 'issued' ? counts.issued : counts.team;
}

/** The page's tabs, in order. Nothing while the Caller summary has not answered (the host shows no switcher then). */
export function describePageTabs(input: PageTabsInput): PageTab[] {
  const keys: PageTabKey[] = [];
  for (const tab of input.tabs) {
    keys.push(tab.key);
    // "Assigned by Me" sits right after the shared tab, for every Caller of the new routes.
    if (tab.key === 'group-shared' && input.mode === 'shared') keys.push('issued');
  }
  return keys.map((key): PageTab => {
    const label = key === 'team-work' && input.mode === 'shared' && input.companyOversight ? COMPANY_TAB_LABEL : TAB_LABELS[key];
    const count = countOf(key, input.counts);
    const scope = key === 'team-work' && input.mode === 'shared' ? (input.companyOversight ? 'everyone in the company' : 'your direct reports') : null;
    // A count the page does not have yet, for a tab it said is pending: say so instead of showing nothing (a blank would read as "no tasks"). A real count always wins.
    const pending = count === null && input.countPending !== undefined && input.countPending.includes(key);
    const title = scope === null ? TITLES[key] : `${TITLES[key]} (${scope})`;
    return {
      key,
      label,
      count,
      accessibleName: count !== null ? `${label}, ${count.phrase}` : pending ? `${label}, ${COUNT_PENDING_PHRASE}` : label,
      title: pending ? `${title}. The count loads when you open this tab.` : title,
      id: `work-tab-${key}`,
      ...(pending ? { countPending: true as const } : {}),
    };
  });
}

/** The tab to show: the selected key if it is still offered (a Caller change may remove Team or Company), else My Tasks. */
export function resolvePageTab(selected: PageTabKey, tabs: readonly Pick<PageTab, 'key'>[]): PageTabKey {
  return tabs.some((tab) => tab.key === selected) ? selected : 'my-work';
}

/** The index the Left / Right / Home / End keys move to (wrapping at the ends); -1 for any other key. */
export function tabKeyTarget(key: string, index: number, length: number): number {
  if (length <= 0) return -1;
  if (key === 'ArrowRight') return index >= length - 1 ? 0 : index + 1;
  if (key === 'ArrowLeft') return index <= 0 ? length - 1 : index - 1;
  if (key === 'Home') return 0;
  if (key === 'End') return length - 1;
  return -1;
}

/** The search box of each tab: its accessible name and placeholder (the Owner's wording). */
export function searchTextFor(key: PageTabKey, companyOversight: boolean): { label: string; placeholder: string } {
  switch (key) {
    case 'my-work':
      return { label: 'Search my tasks', placeholder: 'Search my tasks\u2026' };
    case 'group-shared':
      return { label: 'Search shared tasks', placeholder: 'Search shared tasks\u2026' };
    case 'issued':
      return { label: 'Search requests I assigned', placeholder: 'Search requests I assigned\u2026' };
    default:
      return companyOversight ? { label: 'Search company tasks', placeholder: 'Search company tasks\u2026' } : { label: 'Search team tasks', placeholder: 'Search team tasks\u2026' };
  }
}
